import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { errorText } from '../../utils/errorText';
import { can } from '../../utils/permissions';
import { displayName } from '../../utils/userLabel';
import { box, input, label } from '../shared/styles';

/*
 * User activity (for account administrators: activity:read). Aggregates over
 * the audit ledger and sign-ins: who is active, what they work on, and the
 * recent actions with their reasons -- never the content of a change. Failed
 * sign-ins are counted only; they are never tied to an account.
 */
const ROLES = ['', 'collection_manager', 'internal_expert', 'lab_technician', 'student', 'collector', 'lab_manager',
  'read_only', 'public', 'authorized_researcher', 'iso_auditor', 'system_admin', 'company_executive',
  'external_researcher'];
const MODULES = ['', 'lab', 'research', 'experiments', 'protocols', 'quality', 'mlops', 'accounts', 'other'];
const iso = (d) => d.toISOString().slice(0, 10);

export function DayChart({ days, t }) {
  const W = 640;
  const H = 160;
  const pad = 24;
  const max = Math.max(1, ...days.map((d) => Math.max(d.actions, d.logins)));
  const step = (W - pad * 2) / Math.max(days.length, 1);
  const bar = Math.max(1, step * 0.4);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t('chartLabel')} style={{ width: '100%', maxWidth: W }}
      data-testid="day-chart">
      <line x1={pad} y1={H - pad} x2={W - pad} y2={H - pad} stroke="var(--c-border)" />
      {days.map((d, i) => {
        const x = pad + i * step;
        const ha = ((H - pad * 2) * d.actions) / max;
        const hl = ((H - pad * 2) * d.logins) / max;
        return (
          <g key={d.day}>
            <rect x={x} y={H - pad - ha} width={bar} height={ha} fill="var(--c-primary)" data-testid={`bar-actions-${d.day}`}>
              <title>{`${d.day}: ${d.actions} ${t('actions')}`}</title></rect>
            <rect x={x + bar} y={H - pad - hl} width={bar} height={hl} fill="var(--c-success)">
              <title>{`${d.day}: ${d.logins} ${t('logins')}`}</title></rect>
          </g>
        );
      })}
      <text x={pad} y={H - 6} fontSize="10" fill="var(--c-text-muted-fg)">{days[0]?.day}</text>
      <text x={W - pad} y={H - 6} fontSize="10" textAnchor="end" fill="var(--c-text-muted-fg)">{days[days.length - 1]?.day}</text>
    </svg>
  );
}

function Bars({ rows, labelOf, t }) {
  const max = Math.max(1, ...rows.map((r) => r.actions));
  return (
    <div>{rows.map((r) => (
      <div key={labelOf(r)} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 4 }}>
        <span style={{ width: 140 }}>{t(`module_${labelOf(r)}`, { defaultValue: labelOf(r) })}</span>
        <span style={{ flex: 1, background: 'var(--c-bg-subtle)', borderRadius: 4 }}>
          <span style={{ display: 'block', width: `${(100 * r.actions) / max}%`, background: 'var(--c-primary)', height: 10,
            borderRadius: 4 }} /></span>
        <span style={{ width: 50, textAlign: 'end' }}>{r.actions}</span>
      </div>
    ))}</div>
  );
}

export default function Activity() {
  const { t } = useTranslation('activity');
  const { user } = useAuth();
  // routes/admin_activity.py: activity:read. A deep link from anyone else is
  // told so, as /users tells it (pages/Users.js), and asks the server nothing.
  const allowed = can(user, 'activity:read');
  const today = new Date();
  const [filters, setFilters] = useState({ from: iso(new Date(today.getTime() - 29 * 864e5)), to: iso(today), role: '',
    module: '' });
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    const params = { from: filters.from, to: filters.to, ...(filters.role ? { role: filters.role } : {}) };
    try {
      const [s, r] = await Promise.all([
        api.get('/admin/activity/summary', { params }),
        api.get('/admin/activity/recent', { params: { ...params, ...(filters.module ? { module: filters.module } : {}), limit: 100 } }),
      ]);
      setSummary(s.data);
      setRecent(r.data);
    } catch (e) {
      setError(errorText(e));
    }
  }, [filters]);
  useEffect(() => { if (allowed) load(); }, [load, allowed]);
  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });

  if (!allowed) {
    return <p style={{ color: 'var(--c-text-muted-fg)' }} data-testid="activity-restricted">{t('accessRestricted')}</p>;
  }

  return (
    <div>
      <h2>{t('pageTitle')}</h2>
      <p style={{ color: 'var(--c-text-muted-fg)', marginTop: 0 }}>{t('intro')}</p>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={label}>{t('from')}<input type="date" style={input} value={filters.from} onChange={set('from')} title={t('tip.from')} data-testid="filter-from" /></label>
        <label style={label}>{t('to')}<input type="date" style={input} value={filters.to} onChange={set('to')} title={t('tip.to')} data-testid="filter-to" /></label>
        <label style={label}>{t('role')}
          <select style={input} value={filters.role} onChange={set('role')} title={t('tip.role')} data-testid="filter-role">
            {ROLES.map((r) => <option key={r} value={r}>{r ? t(`role_${r}`, { defaultValue: r }) : t('allRoles')}</option>)}
          </select>
        </label>
        <label style={label}>{t('module')}
          <select style={input} value={filters.module} onChange={set('module')} title={t('tip.module')} data-testid="filter-module">
            {MODULES.map((m) => <option key={m} value={m}>{m ? t(`module_${m}`) : t('allModules')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
      {summary && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12, margin: '12px 0' }}>
            {[['actions', summary.totals.actions], ['active_users', summary.totals.active_users],
              ['logins', summary.totals.logins], ['signed_in_users', summary.totals.signed_in_users],
              ['failed_logins', summary.totals.failed_logins]].map(([k, v]) => (
              <div key={k} style={{ ...box, marginBottom: 0 }}>
                <div style={{ fontSize: 26, fontWeight: 600 }} data-testid={`total-${k}`}>{v}</div>
                <div style={{ fontSize: 13, color: 'var(--c-text-muted-fg)' }}>{t(`total_${k}`)}</div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }}>{t('failedNote')}</p>
          <section style={box}>
            <h4 style={{ marginTop: 0 }}>{t('perDay')}</h4>
            <DayChart days={summary.by_day} t={t} />
            <p style={{ fontSize: 12 }}><span style={{ color: 'var(--c-primary)' }}>■</span> {t('actions')}{' '}
              <span style={{ color: 'var(--c-success)' }}>■</span> {t('logins')}</p>
          </section>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <section style={{ ...box, flex: '1 1 320px' }} data-testid="by-module">
              <h4 style={{ marginTop: 0 }}>{t('byModule')}</h4>
              <Bars rows={summary.by_module} labelOf={(r) => r.module} t={t} />
            </section>
            <section style={{ ...box, flex: '1 1 320px' }}>
              <h4 style={{ marginTop: 0 }}>{t('byRole')}</h4>
              <table style={{ width: '100%', fontSize: 13 }}><tbody>{summary.by_role.map((r) => (
                <tr key={r.role}><td>{t(`role_${r.role}`, { defaultValue: r.role })}</td><td>{r.actions} {t('actions')}</td>
                  <td>{r.users} {t('users')}</td></tr>))}</tbody></table>
            </section>
          </div>
          <section style={box}>
            <h4 style={{ marginTop: 0 }}>{t('topUsers')}</h4>
            <table style={{ width: '100%', fontSize: 13 }} data-testid="top-users">
              <thead><tr><th style={{ textAlign: 'start' }}>{t('user')}</th><th style={{ textAlign: 'start' }}>{t('roles')}</th>
                <th>{t('actions')}</th><th style={{ textAlign: 'start' }}>{t('lastActive')}</th></tr></thead>
              <tbody>{summary.top_users.map((u) => (
                <tr key={u.user_id}><td>{displayName(u.username, t) || '—'}</td><td>{u.roles.join(', ')}</td><td style={{ textAlign: 'center' }}>{u.actions}</td>
                  <td>{new Date(u.last_active).toLocaleString()}</td></tr>))}</tbody>
            </table>
          </section>
        </>
      )}
      <section style={box}>
        <h4 style={{ marginTop: 0 }}>{t('recent')}</h4>
        <table style={{ width: '100%', fontSize: 13 }} data-testid="recent">
          <thead><tr>{['when', 'user', 'action', 'module', 'record', 'reason'].map((c) => <th key={c} style={{ textAlign: 'start' }}>{t(c)}</th>)}</tr></thead>
          <tbody>{recent.map((r, i) => (
            <tr key={`${r.at}-${i}`}><td>{new Date(r.at).toLocaleString()}</td><td>{displayName(r.username, t) || t('system')}</td>
              <td>{r.action}</td><td>{t(`module_${r.module}`)} <small>({r.table})</small></td>
              <td><code>{String(r.record_id || '').slice(0, 12)}</code></td><td>{r.reason || ''}</td></tr>))}</tbody>
        </table>
      </section>
    </div>
  );
}
